import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTimes } from "@fortawesome/free-solid-svg-icons";
import ProductList from "../components/ProductList";
import FilterDropdown from "../components/FilterDropdown";
import SEO from "../components/SEO";
import "../styles/Shop.css";


const TYPE_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Custom made", value: "custom" },
  { label: "Ready to ship", value: "ready-to-ship" },
];

const PRICE_OPTIONS = [
  { label: "Any price", value: "all" },
  { label: "Under $5", value: "under-5" },
  { label: "$5 – $10", value: "5-10" },
  { label: "$10 – $20", value: "10-20" },
  { label: "Over $20", value: "20-plus" },
];

const SORT_OPTIONS = [
  { label: "Featured", value: "featured" },
  { label: "Price, low to high", value: "price-asc" },
  { label: "Price, high to low", value: "price-desc" },
  { label: "Newest", value: "newest" },
  { label: "Alphabetically, A–Z", value: "name-asc" },
  { label: "Alphabetically, Z–A", value: "name-desc" },
];

const Shop = ({ categoryPreselect }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Filters read directly from URL
  const selectedCategory = searchParams.get("category") || categoryPreselect || "All";
  const sortBy = searchParams.get("sort") || "featured";
  const typeFilter = searchParams.get("type") || "all";
  const priceRange = searchParams.get("price") || "all";
  const inStockOnly = searchParams.get("inStock") === "true";

  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [productCount, setProductCount] = useState(null);

  useEffect(() => {
    setSearchQuery(searchParams.get("search") || "");
  }, [searchParams]);

  // If a route specifies a preselected category (e.g. /stickers), reflect it in URL if not set
  useEffect(() => {
    if (categoryPreselect && !searchParams.get("category")) {
      const next = new URLSearchParams(searchParams);
      next.set("category", categoryPreselect);
      setSearchParams(next, { replace: true });
    }
  }, [categoryPreselect, searchParams, setSearchParams]);

  const updateParam = useCallback(
    (key, value, defaultValue = "") => {
      const next = new URLSearchParams(searchParams);
      if (!value || value === defaultValue || value === "All" || value === "all") {
        next.delete(key);
      } else {
        next.set(key, value);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    updateParam("search", value);
  };


  // SEO computation
  const isSearchActive = Boolean(searchQuery.trim());
  const isCategorySelected = selectedCategory && selectedCategory.toLowerCase() !== "all";

  const seoTitle = isSearchActive
    ? `Search: "${searchQuery.trim()}"`
    : isCategorySelected
    ? `${selectedCategory} Collection`
    : "Shop All K-Pop Merch & Handcrafted Goods";

  const seoDescription = isSearchActive
    ? `Search results for "${searchQuery.trim()}" at Star.Ryhwa. Handcrafted K-Pop fan goods.`
    : isCategorySelected
    ? `Shop handcrafted ${selectedCategory.toLowerCase()} at Star.Ryhwa. Handcrafted K-Pop goods with unique designs and fast shipping.`
    : "Shop handcrafted K-pop photocards, custom decoden keychains, vinyl stickers, tote bags, enamel pins, and fanmade collectibles at Star.Ryhwa.";

  const canonicalUrl = isCategorySelected
    ? `/shop?category=${encodeURIComponent(selectedCategory)}`
    : "/shop";

  const pageHeading = isSearchActive
    ? `Search: "${searchQuery.trim()}"`
    : isCategorySelected
    ? `${selectedCategory} Collection`
    : "Handcrafted K-Pop Goods";

  return (
    <section className="shop">
      <SEO
        title={seoTitle}
        description={seoDescription}
        canonicalUrl={canonicalUrl}
      />

      {/* Visually hidden H1 for SEO compliance */}
      <h1
        style={{
          position: "absolute",
          width: "1px",
          height: "1px",
          padding: 0,
          margin: "-1px",
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {pageHeading}
      </h1>

      {/* Main Filter & Sort Toolbar */}
      <div className="shop-toolbar">
        <div className="shop-filters">
          <div className="shop-search">
            <FontAwesomeIcon icon={faSearch} className="shop-search-icon" />
            <input
              id="shop-search"
              type="search"
              placeholder="Search products..."
              aria-label="Search products"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                className="shop-search-clear"
                onClick={() => handleSearchChange("")}
                aria-label="Clear search query"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>

          <FilterDropdown
            id="shop-type"
            label="Type"
            value={typeFilter}
            defaultValue="all"
            options={TYPE_OPTIONS}
            onChange={(v) => updateParam("type", v, "all")}
          />

          <FilterDropdown
            id="shop-price"
            label="Price"
            value={priceRange}
            defaultValue="all"
            options={PRICE_OPTIONS}
            onChange={(v) => updateParam("price", v, "all")}
          />

          <button
            id="shop-in-stock"
            type="button"
            className={`shop-dd-trigger ${inStockOnly ? "is-active" : ""}`}
            aria-pressed={inStockOnly}
            onClick={() => updateParam("inStock", inStockOnly ? "" : "true")}
          >
            In stock
          </button>
        </div>

        <div className="shop-sort">
          {productCount !== null && (
            <span className="shop-count">
              {productCount} {productCount === 1 ? "product" : "products"}
            </span>
          )}
          <FilterDropdown
            id="shop-sort"
            label="Sort"
            value={sortBy}
            options={SORT_OPTIONS}
            onChange={(v) => updateParam("sort", v, "featured")}
            align="right"
            showValueInLabel
          />
        </div>
      </div>

      <ProductList
        searchQuery={searchQuery}
        sortBy={sortBy}
        categoryFilter={selectedCategory}
        typeFilter={typeFilter}
        priceRange={priceRange}
        inStockOnly={inStockOnly}
        onProductCountChange={setProductCount}
        onSearchSelect={(term) => handleSearchChange(term)}
      />
    </section>
  );
};

export default Shop;
